import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import pmmService from '../services/paymentMigrationManagerService';
import OhipProfilePicker from './OhipProfilePicker';
import './AddCorporateProfilePage.css';

const PROFILE_TYPES = [
    { value: 'COM',         label: 'Company' },
    { value: 'TravelAgent', label: 'Travel Agent' },
    { value: 'SRC',         label: 'Source' },
    { value: 'OTA',         label: 'OTA (Online Travel Agent)' },
];

const PMMAddProfileAssignmentPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [form, setForm] = useState({ profile_id: '', profile_name: '', profile_type: 'COM', corporate_id: '' });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined }));
    };

    const handleOhipSelect = (profile) => {
        setForm(f => ({
            ...f,
            profile_id:   profile.profileId   || f.profile_id,
            profile_name: profile.name        || f.profile_name,
            profile_type: profile.profileType || f.profile_type,
            corporate_id: profile.corporateId || f.corporate_id,
        }));
        setErrors({});
    };

    const validate = () => {
        const e = {};
        if (!form.profile_id.trim()) e.profile_id = 'Profile ID is required';
        return e;
    };

    const handleSave = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        try {
            await pmmService.createProfileAssignment(id, {
                profile_id:   form.profile_id.trim(),
                profile_name: form.profile_name.trim() || null,
                profile_type: form.profile_type || null,
                corporate_id: form.corporate_id.trim() || null,
            });
            navigate(`/apps/payment-migration-manager/${id}`);
        } catch (err) {
            setErrors({ submit: err.response?.data?.error?.message || 'Failed to save profile assignment' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div className="acp-card-header">
                    <h1 className="acp-title">Assign OHIP Profile</h1>
                    <p className="acp-subtitle">Assign an OHIP profile to automatically apply it to qualifying reservations</p>
                </div>
                <div className="acp-divider" />
                <div className="acp-body">
                    <div className="acp-field">
                        <label className="acp-label">Search OHIP</label>
                        <OhipProfilePicker
                            searchFn={(name, type) => pmmService.searchOhipProfiles(id, name, type)}
                            onSelect={handleOhipSelect}
                            buttonLabel="Search OHIP Profiles"
                        />
                        <span className="acp-hint">Search OHIP to auto-fill the fields below, or enter them manually.</span>
                    </div>
                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">Profile ID <span className="acp-required">*</span></label>
                            <input className={`acp-input${errors.profile_id ? ' acp-input-error' : ''}`} type="text" placeholder="e.g., 12345" value={form.profile_id} onChange={e => set('profile_id', e.target.value)} />
                            {errors.profile_id && <span className="acp-error-msg">{errors.profile_id}</span>}
                        </div>
                        <div className="acp-field">
                            <label className="acp-label">Profile Type</label>
                            <select className="acp-select" value={form.profile_type} onChange={e => set('profile_type', e.target.value)}>
                                {PROFILE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                {!PROFILE_TYPES.find(t => t.value === form.profile_type) && form.profile_type && (
                                    <option value={form.profile_type}>{form.profile_type}</option>
                                )}
                            </select>
                        </div>
                    </div>
                    <div className="acp-field">
                        <label className="acp-label">Profile Name</label>
                        <input className="acp-input" type="text" placeholder="e.g., Booking.com" value={form.profile_name} onChange={e => set('profile_name', e.target.value)} />
                    </div>
                    <div className="acp-field">
                        <label className="acp-label">Corporate ID</label>
                        <input className="acp-input" type="text" placeholder="OHIP corporate ID (if applicable)" value={form.corporate_id} onChange={e => set('corporate_id', e.target.value)} />
                    </div>
                    {errors.submit && <div className="acp-error-banner">{errors.submit}</div>}
                </div>
                <div className="acp-divider" />
                <div className="acp-footer">
                    <button className="acp-btn-cancel" onClick={() => navigate(`/apps/payment-migration-manager/${id}`)} disabled={saving}>Cancel</button>
                    <button className="acp-btn-save" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Assign Profile'}</button>
                </div>
            </div>
        </div>
    );
};

export default PMMAddProfileAssignmentPage;
